import moment from 'moment';
import Config from 'services/config/local';
import { SHOW_ADJUSTING_LICENSES } from 'services/StudioEvents';
import { loadStripeSDK } from 'services/LazyLoading';

// Inform ES Lint that "Stripe" is a global variable (avoids linter errors)
/* global Stripe */

/* @ngInject */
export default class AddLicensesController {
    constructor(DialogService, ToastService, billingPlan, licensesInUse, hasPriceLevel, reloadBillingPlan, $scope, $rootScope, IAMService, StorageService, ProjectManager) {
        this.DialogService = DialogService;
        this.ToastService = ToastService;

        this.billingPlan = billingPlan
        this.licensesInUse = licensesInUse
        this.hasPriceLevel = hasPriceLevel === true; // Only show prices if explicitly true
        this.reloadBillingPlan = reloadBillingPlan;
        this.$scope = $scope
        this.$rootScope = $rootScope
        this.IAMService = IAMService;
        this.StorageService = StorageService;
        this.ProjectManager = ProjectManager;
    }

    get daysUntilRenewal() {
        const today = moment().startOf('day');
        const renewal = moment(this.renewalDate).startOf('day');
        const days = renewal.diff(today, 'days');
        return days > 0 ? days : 365; // If the renewal date is today, we need to use a full year
    }

    get renewalDateString() {
        const renewalMoment = moment(this.renewalDate);
        return `${renewalMoment.format('MMMM Do')}`;
    }

    get formattedStudioPlan() {
        return this.billingPlan.studioPlan.charAt(0) + this.billingPlan.studioPlan.slice(1).toLowerCase()
    }

    get showProratedSection() {
        return this.sensorLicenses.nextRenewal > this.sensorLicenses.currentlyOwned || this.cellularLicenses.nextRenewal > this.cellularLicenses.currentlyOwned;
    }

    get newSensorLicensesPrice() {
        if (this.sensorLicenses.add > 0) {
            return this.convertToCurrency(this.sensorLicenses.add * (this.pricing.sensor * (this.daysUntilRenewal / 365)).toFixed(2));
        }
        return this.convertToCurrency(0);
    }

    get newCellularLicensesPrice() {
        if (this.cellularLicenses.add > 0) {
            return this.convertToCurrency(this.cellularLicenses.add * (this.pricing.cellular * (this.daysUntilRenewal / 365)).toFixed(2));
        }
        return this.convertToCurrency(0);
    }

    get canPayWithCard() {
        return this.billingPlan.availablePaymentMethods.includes("CARD");
    }

    get canPayWithInvoice() {
        return this.billingPlan.availablePaymentMethods.includes("INVOICE");
    }

    get isZeroPrice() {
        // The backend will return 0 if the price is so small that it can't be prorated
        // We will use this to show a different message to the user
        return this.offer?.priceInCent === 0;
    }

    get hasPricingInformation() {
        return this.hasPriceLevel;
    }

    get canIncreaseSubscriptions() {
        return this.hasPriceLevel;
    }

    get hasValidationError() {
        return this.sensorLicenses.nextRenewal < this.sensorLicenses.inUse ||
               this.cellularLicenses.nextRenewal < this.cellularLicenses.inUse;
    }


    $onInit() {

        // Use the billing plan to set the initial values used for calculating the new values
        this.sensorLicenses = {
            inUse: this.licensesInUse.sensors,
            currentlyOwned: this.billingPlan.sensorLicenses,
            nextRenewal: this.billingPlan.sensorLicensesForRenewal,
            originalNextRenewal: this.billingPlan.sensorLicensesForRenewal,
            add: 0,
            newTotal: this.billingPlan.sensorLicensesForRenewal
        };
        this.cellularLicenses = {
            inUse: this.licensesInUse.cellular,
            currentlyOwned: this.billingPlan.cconLicenses,
            nextRenewal: this.billingPlan.cconLicensesForRenewal,
            originalNextRenewal: this.billingPlan.cconLicensesForRenewal,
            newTotal: this.billingPlan.cconLicensesForRenewal,
            add: 0
        };

        this.showCheckout = false;

        this.editSensorLicenses = false;
        this.editCellularLicenses = false;

        this.pricing = {
            sensor: this.billingPlan.centsPerSensorPerYear / 100,
            cellular: this.billingPlan.centsPerCconPerYear / 100,
        };

        this.renewalDate = new Date(this.billingPlan.nextRenewalDate);

        this.oldAnnualPrice = this.sensorLicenses.currentlyOwned * this.pricing.sensor + this.cellularLicenses.currentlyOwned * this.pricing.cellular;
        this.newAnnualPrice = null;
        this.proratedPrice = null;
        this.loadingOffer = false;
        this.offer = null;
        this.loadingStripe = false;
        this.showInvoiceConfirmDialog = false;
        this.acceptedInvoiceConfirmed = false;
        this.acceptedInvoice = null;
        this.downloadingInvoice = false;
        this.checkoutCompleted = false;

        this.showConfirmLicenseSpinner = false;

        this.updateProratedPrice();
    }

    convertToCurrency(value) {
        if (value === null || !this.hasPriceLevel) {
            return '-';
        }
        return value.toLocaleString(undefined, { style: 'currency', currency: this.billingPlan.currency }); // Uses user locale for formatting when set to 'undefined'
    }

    updateProratedPrice() {
        // Validate that organizations without pricing information can't exceed currently owned subscriptions
        if (!this.hasPriceLevel) {
            if (this.sensorLicenses.nextRenewal > this.sensorLicenses.currentlyOwned) {
                this.sensorLicenses.nextRenewal = this.sensorLicenses.currentlyOwned;
            }
            if (this.cellularLicenses.nextRenewal > this.cellularLicenses.currentlyOwned) {
                this.cellularLicenses.nextRenewal = this.cellularLicenses.currentlyOwned;
            }
        }

        if (this.sensorLicenses.nextRenewal < this.sensorLicenses.inUse) {
            this.proratedPrice = null
            return
        }
        if (this.cellularLicenses.nextRenewal < this.cellularLicenses.inUse) {
            this.proratedPrice = null
            return
        }

        this.sensorLicenses.newTotal = this.sensorLicenses.nextRenewal
        this.cellularLicenses.newTotal = this.cellularLicenses.nextRenewal
        this.sensorLicenses.add = Math.max(0, this.sensorLicenses.nextRenewal - this.sensorLicenses.currentlyOwned);
        this.cellularLicenses.add = Math.max(0, this.cellularLicenses.nextRenewal - this.cellularLicenses.currentlyOwned);

        const newAnnualPriceCheck = this.sensorLicenses.newTotal * this.pricing.sensor + this.cellularLicenses.newTotal * this.pricing.cellular;
        if (newAnnualPriceCheck === this.oldAnnualPrice) {
            this.newAnnualPrice = null;
        } else {
            this.newAnnualPrice = this.sensorLicenses.newTotal * this.pricing.sensor + this.cellularLicenses.newTotal * this.pricing.cellular;
        }

        const newSensorLicenses = this.sensorLicenses.add
        const newCellularLicenses = this.cellularLicenses.add
        
        const sensorCost = newSensorLicenses * (this.pricing.sensor * (this.daysUntilRenewal / 365)).toFixed(2);
        const cellularCost = newCellularLicenses * (this.pricing.cellular * (this.daysUntilRenewal / 365)).toFixed(2);
        this.proratedPrice = sensorCost + cellularCost
    }

    incrementLicenses(type, increment) {
        if (type === 'sensor') {
            const newValue = this.sensorLicenses.nextRenewal + increment;
            // Prevent increasing beyond currently owned if no pricing information available
            if (!this.hasPriceLevel && newValue > this.sensorLicenses.currentlyOwned) {
                return; // Block the increment
            }
            this.sensorLicenses.nextRenewal = newValue;
        } else if (type === 'cellular') {
            const newValue = this.cellularLicenses.nextRenewal + increment;
            // Prevent increasing beyond currently owned if no pricing information available
            if (!this.hasPriceLevel && newValue > this.cellularLicenses.currentlyOwned) {
                return; // Block the increment
            }
            this.cellularLicenses.nextRenewal = newValue;
        }
        this.updateProratedPrice();
    }

    confirmLicenses() {
        if (this.hasValidationError) return;
        this.editSensorLicenses = false;
        this.editCellularLicenses = false;

        // We need to check if the user is just updating the the amount of licenses for renewal
        // or if they are purchasing new licenses. If the licenses amount is below currently owned,
        // we are just updating the amount of licenses for renewal.

        const sensorLicensesChanged = this.sensorLicenses.nextRenewal !== this.sensorLicenses.originalNextRenewal;
        const cellularLicensesChanged = this.cellularLicenses.nextRenewal !== this.cellularLicenses.originalNextRenewal;

        // See what part of the licenses that can just be updated without purchasing new ones

        if (sensorLicensesChanged || cellularLicensesChanged) {
            this.showConfirmLicenseSpinner = true;
            const updatedLicensesForRenewal = {};
            if (sensorLicensesChanged) {
                // This can't exceed the currently owned licenses
                updatedLicensesForRenewal.sensorLicensesForRenewal = Math.min(this.sensorLicenses.nextRenewal, this.sensorLicenses.currentlyOwned);
            }
            if (cellularLicensesChanged) {
                // This can't exceed the currently owned licenses
                updatedLicensesForRenewal.cconLicensesForRenewal = Math.min(this.cellularLicenses.nextRenewal, this.cellularLicenses.currentlyOwned);
            }
            this.IAMService.updateLicensesForRenewal(updatedLicensesForRenewal).then(() => {
                this.showConfirmLicenseSpinner = false;
                if (!this.showProratedSection) {
                    // The user is not purchasing new licenses, so we can close the modal
                    this.ToastService.showSimpleText("Licenses updated successfully");
                    this.closeModal();
                }
            }).catch((error) => {
                this.showConfirmLicenseSpinner = false;
                this.ToastService.showSimpleText(`Error updating licenses ${error}`);
            });
        }

        if (this.showProratedSection) {
            // Check if pricing information is available for the organization
            if (!this.hasPriceLevel) {
                this.ToastService.showSimpleText("No pricing information available for your organization");
                return;
            }
            
            // The user is purchasing new licenses, so we need to show the payment options
            this.readyToPurchase = true;

            const offer = {
                sensorLicenses: Math.max(this.sensorLicenses.add, 0),
                cellularLicenses: Math.max(this.cellularLicenses.add, 0),
                estimatedProratedPriceCents: Math.round(this.proratedPrice * 100),
            }

            this.generateOffer(offer);
        }
    }

    cancelPurchaseFlow() {
        this.readyToPurchase = false
        this.showInvoiceConfirmDialog = false
        if (this.sensorLicenses.nextRenewal !== this.sensorLicenses.originalNextRenewal) {
            this.editSensorLicenses = true;
        }
        if (this.cellularLicenses.nextRenewal !== this.cellularLicenses.originalNextRenewal) {
            this.editCellularLicenses = true;
        }
    }

    generateOffer(offer) {

        // Request the purchase of new licenses. This will set up an offer and an invoice in 
        // Netsuite. If the payment method is CARD, a payment URL will be returned which in
        // turn can be converted to an embedded checkout. If the payment method is INVOICE,
        // the invoice will be returned and the customer will need to pay it manually.

        this.loadingOffer = true;
        this.IAMService.generateLicenseOffer(offer).then((response) => {
            this.offer = response.offer;
        }).catch((error) => {
            this.ToastService.showSimpleText(`Error generating offer: ${error}`);
            this.offer = null;
            this.readyToPurchase = false;
        }).finally(() => {
            this.loadingOffer = false
        })
    }

    payByCard() {
        this.loadingStripe = true;
        this.purchaseLicenses(this.offer, "CARD");
    }

    payWithInvoice() {
        this.showInvoiceConfirmDialog = true
        // Scroll invoice element into view
        setTimeout(() => {
            const invoiceConfirmDialog = document.getElementById("invoiceConfirmDialog");
            if (invoiceConfirmDialog) {
                invoiceConfirmDialog.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 100); // Small delay to ensure DOM is updated
    }
    
    confirmInvoicePayment() {
        this.purchaseLicenses(this.offer, "INVOICE");
    }

    cancelInvoicePayment() {
        this.showInvoiceConfirmDialog = false
    }

    async purchaseLicenses(offer, paymentMethod) {
    
        if (paymentMethod === "CARD") {
            // Convert the payment URL to a client secret to be used with the embedded checkout
            const embeddedCheckoutURL = `${offer.paymentUrl}?embed=true`;
            const embedResp = await fetch(embeddedCheckoutURL)
            if (!embedResp.ok) {
                this.ToastService.showSimpleText("Error retrieving payment information");
                return;
            }
            const embedData = await embedResp.json();
            const clientSecret = embedData.clientSecret;

            this.showCheckout = true;
            // Load the Stripe SDK (only if payment method is CARD)
            await loadStripeSDK();
            const stripeKey = offer.subsidiary === "US" ? Config.stripe.usPublishableKey : Config.stripe.euPublishableKey;
            const stripeClient = Stripe(stripeKey);
            
            this.checkout = await stripeClient.initEmbeddedCheckout({
                fetchClientSecret: () => {
                    return clientSecret;
                },
                onComplete: () => {
                    this.loadingStripe = false;
                    this.checkoutCompleted = true;
                    this.showActivatingLicensesBanner();
                    this.$scope.$applyAsync()
                }
            });
            this.checkout.mount('#stripe-checkout');
            this.$scope.$applyAsync()
        } else if (paymentMethod === "INVOICE") {
            this.loadingAcceptOffer = true;

            this.IAMService.acceptLicenseOffer(offer.offerId).then((response) => {
                this.acceptedInvoiceConfirmed = true;
                this.acceptedInvoice = response.invoice;
                this.showActivatingLicensesBanner();
            }).catch((error) => {
                this.ToastService.showSimpleText(`Error purchasing licenses: ${error}`);
            }
            ).finally(() => {
                this.loadingAcceptOffer = false;
            });

        } else {
            console.error("Unknown payment method"); // eslint-disable-line no-console
        }
    }

    showActivatingLicensesBanner() {
        this.StorageService.setItem("ADJUSTING_LICENSES", {
            "timestamp": new Date().getTime(),
            "organizationId": this.ProjectManager.currentProject.organizationId,
            "newSensorLicenseCount": this.sensorLicenses.nextRenewal,
            "newCellularLicenseCount": this.cellularLicenses.nextRenewal
        });

        this.$rootScope.$broadcast(SHOW_ADJUSTING_LICENSES)
    }
    

    cancelCheckout() {
        if (this.checkout) {
            this.checkout.destroy();
        }
        this.loadingStripe = false;
        this.showCheckout = false;
        this.$scope.$applyAsync()
    }

    downloadPDF() {
        const invoiceNumber = this.acceptedInvoice.invoiceNumber;
        this.downloadingInvoice = true;
        this.IAMService.generateInvoicePDF(invoiceNumber).then(response => {
            
            // Create a link element, hide it, direct it towards the blob, and then 'click' it programatically
            const a = document.createElement("a");
            a.style = "display: none";
            document.body.appendChild(a);
            // Create a DOMString representing the blob and point the link element towards it
            const url = window.URL.createObjectURL(response.data);
            a.href = url;
            a.download = `${invoiceNumber}.pdf`;
            a.click(); // Programatically click the link to trigger the download
            // Release the reference to the file by revoking the Object URL
            window.URL.revokeObjectURL(url);
            this.downloadingInvoice = false;
        })
    }

    closeModal() {
        this.reloadBillingPlan()
        this.DialogService.cancel();
    }

    $onDestroy() {
        if (this.checkout) {
            this.checkout.destroy();
        }
    }
}